import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ActionButton from "../../../component/common/ActionButton";
import AttachmentPreviewList from "../../../component/common/AttachmentPreviewList";
import FormModal from "../../../component/common/FormModal";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";
import PendingFileList from "./PendingFileList";
import { workRequestApi } from "../api/workRequestApi";
import { runApiAction } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;
// 정렬 순서·사용 여부는 화면에서 다루지 않는다 — 양식은 "올려두면 요청 화면에 뜬다" 가 전부라
// 옵션이 늘수록 등록이 번거로워진다. 백엔드는 기본값(sortOrder=0, active=true)을 쓴다.
const EMPTY_FORM = { id: null, deptId: "", name: "", description: "", files: [], formats: [] };

/**
 * 요청 양식 탭 — 팀별 요청 유형 카드 목록.
 *
 * <p>여기 등록한 양식이 요청 작성 화면의 <b>제출 서류 체크리스트</b>가 된다.
 * 양식 하나 = 요청자가 채워서 낼 서류 한 칸.
 *
 * <p><b>카드를 누르면 수정 모달이 열린다.</b> 카드마다 '양식 추가·수정·삭제' 버튼을 늘어놓으면
 * 목록이 버튼 밭이 되어 정작 어느 팀에 무슨 양식이 있는지가 안 읽힌다. 편집은 모달 안으로 모은다.
 *
 * <p>권한(서버도 동일): 조회는 전 직원, 등록·양식 추가는 내 팀, 수정·삭제는 내가 만든 것.
 * 전체 관리자는 모든 팀. 편집 권한이 없으면 카드가 안 눌린다(읽기 전용).
 */
export default function WorkRequestTypeAdminTab({ departments = [], me, isAdmin = false }) {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const myDeptId = me?.deptId ?? null;   // /employees/me → EmployeeMeResponseDto (id·deptId 평면 구조)
    const canManageDept = (deptId) =>
        isAdmin || (myDeptId != null && String(deptId) === String(myDeptId));
    /** 등록자 정보가 사라진 항목은 같은 팀이 정리할 수 있게 열어둔다(서버와 동일 규칙). */
    const isMine = (ownerId, deptId) =>
        isAdmin || (me?.id != null && (ownerId === me.id || (ownerId == null && canManageDept(deptId))));
    const canCreate = isAdmin || myDeptId != null;

    const load = useCallback(() => {
        setLoading(true);
        workRequestApi.getTypes()          // deptId 없이 = 전 팀 전체(관리 화면)
            .then(list => setTypes(Array.isArray(list) ? list : []))
            .catch(() => setTypes([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const map = new Map();
        types.forEach(t => {
            const key = t.deptName || "미지정";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(t);
        });
        return [...map.entries()];
    }, [types]);

    const openCreate = () => setForm({ ...EMPTY_FORM, deptId: myDeptId ?? "" });
    const openEdit = (t) => setForm({
        id: t.id, deptId: t.deptId ?? "", name: t.name,
        description: t.description || "", files: [], formats: t.formats || [],
    });

    /** 저장 — 유형 저장 후 새로 고른 양식 파일을 이어서 올린다. */
    const save = () => runApiAction({
        request: async () => {
            const payload = {
                deptId: Number(form.deptId),
                name: form.name.trim(),
                description: form.description.trim() || null,
            };
            const saved = form.id ? await workRequestApi.updateType(form.id, payload)
                                  : await workRequestApi.createType(payload);
            for (const file of form.files || []) {
                await workRequestApi.uploadFormat(saved.id, file);
            }
            return saved;
        },
        setLoading: setSaving,
        successMsg: form.id ? "요청 유형을 수정했습니다." : "요청 유형과 양식을 등록했습니다.",
        onSuccess: () => { setForm(null); load(); },
    });

    const remove = async () => {
        const ok = await confirmDialog({
            title: "요청 유형 삭제",
            message: `'${form.name}' 과(와) 등록된 양식 파일이 삭제됩니다.\n`
                   + "이 유형으로 만든 기존 요청서는 남지만 유형 표시가 사라집니다.\n\n계속할까요?",
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        runApiAction({
            request: () => workRequestApi.removeType(form.id),
            setLoading: setSaving,
            successMsg: "요청 유형을 삭제했습니다.",
            onSuccess: () => { setForm(null); load(); },
        });
    };

    /** 모달 안에서 기존 양식 삭제 — 지운 결과가 바로 보이게 목록에서도 빼준다. */
    const removeExistingFormat = (formatId) => runApiAction({
        request: () => workRequestApi.removeFormat(formatId),
        successMsg: "양식 파일을 삭제했습니다.",
        onSuccess: () => {
            setForm(f => ({ ...f, formats: (f.formats || []).filter(x => x.id !== formatId) }));
            load();
        },
    });

    const editable = form && (form.id == null || isMine(
        types.find(t => t.id === form.id)?.createdById, form.deptId));

    return (
        <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ flex: 1, fontSize: 12.5, color: C.textMuted, minWidth: 0 }}>
                    여기 등록한 양식이 요청 작성 화면의 제출 서류 목록이 됩니다.
                    {isAdmin ? " 관리자는 모든 팀에 등록할 수 있습니다." : ""}
                </Typography>
                {canCreate && (
                    <ActionButton
                        variant="primary" size="small"
                        startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                        onClick={openCreate}
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}   // 없으면 라벨이 두 줄로 깨진다
                    >
                        요청 유형 등록
                    </ActionButton>
                )}
            </Stack>

            {loading && <LoadingDots />}

            {!loading && types.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center", borderRadius: 2, border: `1px dashed ${C.border}`, bgcolor: "#fafbfc" }}>
                    <Typography sx={{ fontSize: 13, color: C.textMuted, mb: 1.5 }}>
                        등록된 요청 유형이 없습니다. 유형이 없으면 직원이 요청서를 작성할 수 없습니다.
                    </Typography>
                    {canCreate && (
                        <ActionButton variant="primary" size="small" onClick={openCreate}>
                            첫 요청 유형 등록하기
                        </ActionButton>
                    )}
                </Box>
            )}

            <Stack spacing={2.5}>
                {grouped.map(([deptName, list]) => (
                    <Box key={deptName}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, mb: 1 }}>
                            {deptName} <Box component="span" sx={{ color: C.textMuted, fontWeight: 400 }}>({list.length})</Box>
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 1.5 }}>
                            {list.map(t => {
                                const canEdit = isMine(t.createdById, t.deptId);
                                return (
                                    <TypeCard
                                        key={t.id}
                                        type={t}
                                        canEdit={canEdit}
                                        onClick={canEdit ? () => openEdit(t) : undefined}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Stack>

            <FormModal
                open={Boolean(form)}
                onClose={() => setForm(null)}
                title={form?.id ? "요청 유형 수정" : "요청 유형 등록"}
                subtitle="여기 올린 양식 하나하나가 요청자가 채워서 낼 서류 한 칸이 됩니다."
                maxWidth="sm"
                saving={saving}
                saveDisabled={!form?.deptId || !form?.name?.trim()}
                onSave={save}
                footerExtras={form?.id && editable ? (
                    <ActionButton variant="delete" size="small" loading={saving} onClick={remove} />
                ) : null}
            >
                <Stack spacing={2.5}>
                    {/* 관리자는 모든 팀에 등록 가능 → 드롭다운. 일반 직원은 내 소속으로 고정(서버도 거부). */}
                    <FormField label="팀" required={isAdmin}>
                        {isAdmin ? (
                            <TextField
                                select fullWidth size="small"
                                value={form?.deptId ?? ""}
                                onChange={(e) => setForm(f => ({ ...f, deptId: e.target.value }))}
                                helperText="관리자는 모든 팀의 양식을 등록·수정할 수 있습니다."
                            >
                                {departments.map(d => (
                                    <MenuItem key={d.deptId} value={d.deptId} sx={{ fontSize: 13 }}>
                                        {d.deptName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <TextField
                                fullWidth size="small" disabled
                                value={departments.find(d => String(d.deptId) === String(form?.deptId))?.deptName || "-"}
                                helperText="본인 소속 팀의 양식만 등록·수정할 수 있습니다."
                            />
                        )}
                    </FormField>

                    <FormField label="유형명" required>
                        <TextField
                            fullWidth size="small"
                            value={form?.name ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="예: 영상제작요청서"
                            inputProps={{ maxLength: 150 }}
                        />
                    </FormField>

                    <FormField label="작성 안내">
                        <TextField
                            fullWidth multiline minRows={3} size="small"
                            value={form?.description ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="요청자가 작성 화면에서 보게 될 안내 문구입니다."
                        />
                    </FormField>

                    {/* 이미 올라간 양식 — 여기서 바로 지울 수 있다(목록 카드에는 버튼을 두지 않는다) */}
                    {form?.formats?.length > 0 && (
                        <FormField label={`등록된 양식 ${form.formats.length}개`}>
                            <AttachmentPreviewList
                                attachments={form.formats}
                                previewFn={workRequestApi.previewFormat}
                                onDelete={removeExistingFormat}
                                canDelete={(f) => isMine(f.uploaderId, form.deptId)}
                                showUploader
                            />
                        </FormField>
                    )}

                    <FormField label="양식 추가">
                        <PendingFileList
                            files={form?.files || []}
                            onChange={(files) => setForm(f => ({ ...f, files }))}
                            label="양식 파일 선택"
                            helper="pdf · xlsx · ppt 등 (여러 개 가능)"
                        />
                        <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 0.75 }}>
                            요청자는 각 양식과 <b>같은 형식</b>으로만 제출할 수 있습니다(파일명은 자유).
                        </Typography>
                    </FormField>
                </Stack>
            </FormModal>
        </Box>
    );
}

/** 요청 유형 카드 — 유형명·안내·양식 수·등록자. 양식이 0개면 경고색으로 눈에 띄게. */
function TypeCard({ type, canEdit, onClick }) {
    const count = type.formats?.length || 0;
    const empty = count === 0;
    return (
        <Box
            onClick={onClick}
            sx={{
                p: 1.75, borderRadius: 2, height: "100%",
                border: `1px solid ${empty ? "#fecaca" : C.border}`,
                bgcolor: "#fff",
                cursor: canEdit ? "pointer" : "default",
                transition: "border-color .15s, box-shadow .15s",
                ...(canEdit ? {
                    "&:hover": { borderColor: C.primaryBorder, boxShadow: "0 2px 10px rgba(15,23,42,0.06)" },
                } : {}),
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.75 }}>
                <DescriptionRoundedIcon sx={{ fontSize: 18, color: C.primary, flexShrink: 0, mt: 0.2 }} />
                <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.textPrimary, minWidth: 0 }}>
                    {type.name}
                </Typography>
                <Chip
                    icon={<AttachFileRoundedIcon sx={{ fontSize: 13 }} />}
                    label={count}
                    size="small"
                    sx={{
                        height: 20, fontSize: 11, fontWeight: 700, flexShrink: 0,
                        bgcolor: empty ? "#fef2f2" : C.primaryBg,
                        color: empty ? "#b91c1c" : C.primaryText,
                    }}
                />
            </Stack>

            {type.description && (
                <Typography sx={{
                    fontSize: 12, color: C.textSecondary, mb: 1,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                    {type.description}
                </Typography>
            )}

            {empty ? (
                <Typography sx={{ fontSize: 11, color: "#b91c1c", fontWeight: 600 }}>
                    양식 파일이 없습니다 — 요청자가 받을 파일이 없습니다
                </Typography>
            ) : (
                <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                    {type.createdByName}{canEdit ? " · 눌러서 수정" : ""}
                </Typography>
            )}
        </Box>
    );
}
